import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { pdfServiceClient } from '../api/pdfClient';

interface PDFGenerationData {
  candidate_id: string;
  requirement_id: string;
  include_sensitive_data?: boolean;
  report_format?: string;
}

interface PDFGenerationResponse {
  success: boolean;
  download_url?: string;
  filename?: string;
  s3_key?: string;
  generated_at?: string;
  message?: string;
}

export const useGeneratePDF = () => {
  return useMutation({
    mutationFn: async (data: PDFGenerationData): Promise<PDFGenerationResponse> => {
      return await pdfServiceClient.generateJDCAnalysisPDF(data);
    },
    onSuccess: (data) => {
      if (data.success && data.download_url) {
        toast.success('PDF report generated successfully! Opening in new tab...', {
          duration: 3000
        });
        
        // Open PDF in new tab instead of downloading
        window.open(data.download_url, '_blank');
      } else {
        toast.error(data.message || 'Failed to generate PDF report');
      }
    },
    onError: (error: any) => {
      console.error('PDF generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate PDF report');
    }
  });
};

export const usePDFGeneration = () => {
  const generatePDF = useGeneratePDF();

  const handleGeneratePDF = async (
    candidateId: string,
    requirementId: string,
    options?: {
      includeSensitiveData?: boolean;
      reportFormat?: string;
    }
  ) => {
    try {
      // Show loading message
      toast.loading('Generating PDF report... This may take a few moments.', {
        duration: 0, // Keep loading until manually dismissed
        id: 'pdf-generation' // Use a consistent ID to update the same toast
      });
      
      await generatePDF.mutateAsync({
        candidate_id: candidateId,
        requirement_id: requirementId,
        include_sensitive_data: options?.includeSensitiveData || false,
        report_format: options?.reportFormat || 'comprehensive'
      });
      
      // Dismiss the loading toast
      toast.dismiss('pdf-generation');
    } catch (error) {
      // Dismiss the loading toast on error
      toast.dismiss('pdf-generation');
      // Error handling is done in the mutation
      console.error('PDF generation failed:', error);
    }
  };

  return {
    generatePDF: handleGeneratePDF,
    isGenerating: generatePDF.isPending,
    error: generatePDF.error,
    isSuccess: generatePDF.isSuccess
  };
};
